import React, { ChangeEvent, FocusEvent, useContext, useState } from 'react';

import { observer } from 'mobx-react-lite';

import { RootStoreContext } from 'index';
import Button from 'components/common/Button/Button';
import useForm, { FieldValues } from 'hooks/useForm';
import { validateEmail, validatePassword } from 'components/auth/authValidation.utils';
import { UpdatePasswordErrorMessage } from 'components/auth/auth.dto';
import { notify } from 'components/common/Toast/toast.helpers';
import { ToastMessage } from 'components/common/Toast/toast.enums';
import TextInput from 'components/common/TextInput/TextInput';
import PasswordInput from 'components/common/PasswordInput/PasswordInput';
import ConfirmationCode from 'components/common/ConfirmationCode/ConfirmationCode';

import styles from './UpdateAccountEmail.module.scss';
import { UpdateEmailStep } from './updateAccountEmail.enums';

const UpdateAccountEmail = ({ onClose }: { onClose: () => void }) => {
  const { authStore } = useContext(RootStoreContext);
  const [step, setStep] = useState<UpdateEmailStep>(UpdateEmailStep.ConfirmPassword);
  const [accountPasswordValidationToken, setAccountPasswordValidationToken] = useState<string>('');
  const {
    authStore: { isLoading, isSuccessValidation },
  } = useContext(RootStoreContext);

  const { errors, values, setValue, validateField, handleSubmit, setErrors } = useForm<FieldValues>({
    defaultValues: {
      [UpdateEmailStep.ConfirmPassword]: '',
      [UpdateEmailStep.NewEmail]: '',
      [UpdateEmailStep.ConfirmationCode]: '',
    },
    handleValidation: (fieldName, value) => {
      switch (true) {
        case fieldName === UpdateEmailStep.ConfirmPassword && step === UpdateEmailStep.ConfirmPassword: {
          return validatePassword(value);
        }
        case fieldName === UpdateEmailStep.NewEmail && step === UpdateEmailStep.NewEmail: {
          return validateEmail(value, fieldName);
        }
        default:
          return '';
      }
    },
  });

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setValue(name, value);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    const name = event.target.name as keyof typeof values;

    validateField(name, values[name]);
  };

  const handlePassword = handleSubmit(async () => {
    const result = await authStore.verifyPassword(values[UpdateEmailStep.ConfirmPassword]);

    if (result.success && result.accountPasswordValidationToken) {
      setStep(UpdateEmailStep.NewEmail);
      setAccountPasswordValidationToken(result.accountPasswordValidationToken);
    } else {
      if (result.message === UpdatePasswordErrorMessage.InvalidPassword) {
        setErrors({
          [UpdateEmailStep.ConfirmPassword]: 'Invalid Password. Please check your Password and try again',
        });
      } else {
        notify(ToastMessage.DefaultError, 'error');
      }
    }
  });

  const handleEmail = handleSubmit(async () => {
    const result = await authStore.sendUpdateEmailVerificationCode(
      values[UpdateEmailStep.NewEmail],
      accountPasswordValidationToken,
    );

    if (result.success) {
      setStep(UpdateEmailStep.ConfirmationCode);
    } else {
      notify(ToastMessage.DefaultError, 'error');
    }
  });

  const sendVerificationCode = async () => {
    await authStore.sendUpdateEmailVerificationCode(values[UpdateEmailStep.NewEmail], accountPasswordValidationToken);
  };

  const verifyVerificationCode = async (code: string) => {
    const result = await authStore.updateEmailAddressByVerifyingCode(values[UpdateEmailStep.NewEmail], code);

    if (result.success) {
      await authStore.getAccount();
      onClose();
      notify(ToastMessage.UpdatedEmail, 'success');
    }
  };

  const submitHandlersByStep = {
    [UpdateEmailStep.ConfirmPassword]: handlePassword,
    [UpdateEmailStep.NewEmail]: handleEmail,
  };

  const getStep = () => {
    if (step === UpdateEmailStep.ConfirmPassword) {
      return (
        <PasswordInput
          labelTitle={UpdateEmailStep.ConfirmPassword}
          name={UpdateEmailStep.ConfirmPassword}
          tabIndex={2}
          value={values[UpdateEmailStep.ConfirmPassword]}
          containerClassName={styles.passwordInputContainer}
          errorMessage={errors[UpdateEmailStep.ConfirmPassword]}
          onChange={handleChange}
          onBlur={handleBlur}
          required
        />
      );
    } else if (step === UpdateEmailStep.NewEmail) {
      return (
        <TextInput
          className={styles.input}
          containerClassName={styles.inputContainer}
          labelTitle={UpdateEmailStep.NewEmail}
          name={UpdateEmailStep.NewEmail}
          value={values[UpdateEmailStep.NewEmail]}
          errorMessage={errors[UpdateEmailStep.NewEmail]}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      );
    } else if (step === UpdateEmailStep.ConfirmationCode) {
      return (
        <div className={styles.confirmationCode}>
          <p className={styles.text}> We've sent a 6-digit verification code to {values[UpdateEmailStep.NewEmail]}.</p>
          <ConfirmationCode
            isLoading={isLoading}
            codeLength={6}
            isSuccessCode={isSuccessValidation}
            sendVerificationCode={sendVerificationCode}
            verifyVerificationCode={verifyVerificationCode}
          />
        </div>
      );
    }

    return <></>;
  };

  return (
    <>
      {getStep()}
      {step !== UpdateEmailStep.ConfirmationCode && (
        <Button className={styles.sendButton} onClick={submitHandlersByStep[step]} isLoading={isLoading}>
          Submit
        </Button>
      )}
    </>
  );
};

export default observer(UpdateAccountEmail);
